import { ordersActions } from "../../../_store/order.slice";
import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { LoadingSpinner } from "../../../_components/Abstractions/LoadingSpinner";
import { DomainButton } from "../../../_components/Abstractions/DomainButton";
import { Alert, Card } from "react-bootstrap";
import { TableDomain } from "../../../_components/Abstractions/TableDomain";
import { WasteFormType } from "../../../domain/WasteFormType";
import { ServicePricingType } from "../../../domain/ServicePricingType";
import { EditRatePlanToOrderForm } from "./EditRatePlanToOrder";
import { FormModal } from "../../../_components/Forms/FormModal";
import { useFormContext } from "react-hook-form";
import { BasicSwitch } from "../../../_components/Abstractions/BasicSwitch";
import { ButtonOverlay } from "../../../_components/Abstractions/ButtonOverlay";
import { SelectSearcher } from "../../../_components/Abstractions/MultiSearcher/SelectSearcher";
import { orderViewsActions } from "../../../_store/orderView.slice";
import * as Yup from "yup";
import { TextArea } from "../../../_components/Forms/TextArea";

export function AttachRatePlanToOrder({ orderId, orderRatePlan, isUpdate }) {
  const dispatch = useDispatch();
  const validationSchema = Yup.object().shape({
    orderRatePlan: Yup.object().required("Cennik jest wymagany").typeError("Cennik jest wymagany"),
  });
  const formControls = <>
    <AttachRatePlanToOrderForm orderId={orderId} isUpdate={isUpdate}/>
  </>;


  const onSubmit = async(data) => {
    const { orderRatePlan } = data;
    const records = [].concat(orderRatePlan.wastes ?? []).concat(orderRatePlan.services ?? []);
    if (records.some(x => x.price === null)) {
      return { error: { message: "Wszystkie rekordy w cenniku powinny mieć uzupełnioną cenę." } };
    }

    const payload = {
        ratePlanId: orderRatePlan.ratePlanId,
        description: orderRatePlan.description,
        wastes: orderRatePlan.wastes,
        services: orderRatePlan.services,
    }
    const response = await dispatch(ordersActions.attachRatePlanToOrder({ orderId, payload }));
    if (!response?.error) {
      await dispatch(orderViewsActions.getOrderRatePlan({ orderId }));
      await dispatch(orderViewsActions.refreshHistory({ id: orderId }));
    }

    return response;
  };

  return <FormModal
    buttonText={isUpdate ? "Podsumowanie cennika" : "Dołącz cennik"}
    formControls={formControls}
    onSubmit={onSubmit}
    btnSize={isUpdate ? "xx-small" : "small"}
    defaultValues={isUpdate ? { orderRatePlan: orderRatePlan } : null}
    validationSchema={validationSchema}
    title="Dołącz cennik pod zlecenie"
    sizeModal="lg"
    successMessage="Cennik został podpięty pod zlecenie"
  />;
}

export const AttachRatePlanToOrderForm = ({ orderId, isUpdate }) => {
  const dispatch = useDispatch();
  const { setValue } = useFormContext();
  const [isLoading, setIsLoading] = useState(false);
  const [isManualMode, setIsManualMode] = useState(false);
  const [orderRatePlans, setOrderRatePlans] = useState([]);
  const refreshRatePlan = async(isUpdate = false) => {
    if (!isUpdate) {
      setIsLoading(true);
      const { payload: orderRatePlan } = await dispatch(ordersActions.getRatePlanForOrder({ orderId }));
      setValue("orderRatePlan", orderRatePlan);
    }

    const { payload: ratePlans } = await dispatch(ordersActions.getRatePlansForOrder({ orderId }));
    setOrderRatePlans(ratePlans);

    setIsLoading(false);
  };

  useEffect(() => {
    refreshRatePlan(isUpdate);
  }, []);

  return <>
    <div className="d-flex gap-2 mb-2 align-items-center">
      <DomainButton onClick={() => refreshRatePlan(false)}>Odśwież cennik</DomainButton>
      <BasicSwitch label="Ręczna zmiana cennika" value={isManualMode} onChange={setIsManualMode} skipFormMargin/>
    </div>
    <LoadingSpinner isLoading={isLoading} as="div"/>
    {!isManualMode ?
      <OrderRatePlanPreviewDetails isLoading={isLoading} ratePlans={orderRatePlans}/> : <EditRatePlanToOrderForm />}
  </>;
};

const OrderRatePlanPreviewDetails = ({ isLoading, ratePlans }) => {
  const { watch } = useFormContext();
  const orderRatePlan = watch("orderRatePlan");

  if (isLoading) {
    return;
  }

  const serviceColumns = [
    { name: "description", headerName: "Opis" },
    { name: "wasteDescription", headerName: "Towar", map: (service) => service.wasteDescription ?? "-" },
    {
      name: "pricingType",
      headerName: "Jednostka",
      map: (service) => service.pricingType ? ServicePricingType[service.pricingType] : "-"
    },
    { name: "price", headerName: "Cena", map: (service) => service.price ?? "-" },
    { name: "remarks", headerName: "Uwagi",
      map: (service) =>
        service.price === null ? "Brak ceny" :
          service.priceChangeReason ? <ButtonOverlay variant="secondary" className="mb-1" placement="bottom" tooltip={service.priceChangeReason?.toUpperCase()}>Zmiana ceny</ButtonOverlay> : null
    },
  ];

  const wasteColumns = [
    { name: "description", headerName: "Opis" },
    {
      name: "wasteForm",
      headerName: "Forma",
      map: (waste) => waste.wasteForm ? WasteFormType[waste.wasteForm] : "-"
    },
    {
      name: "exchangeType",
      headerName: "Załadunek/Kontener",
      map: (waste) => waste.exchangeType === "Container" ? "Kontener" : "Załadunek"
    },
    { name: "price", headerName: "Cena", map: (service) => service.price ?? "-" },
    { name: "remarks", headerName: "Uwagi",
      map: (waste) =>
        waste.price === null ? "Brak ceny" :
        waste.priceChangeReason ? <ButtonOverlay variant="secondary" className="mb-1" placement="bottom" tooltip={waste.priceChangeReason?.toUpperCase()}>Zmiana ceny</ButtonOverlay> : null
    },
  ];

  return <Card>
    <Card.Header as="b">
      {ratePlans.length === 0 && !orderRatePlan?.ratePlanId && <Alert variant="warning">Nie znaleziono pasującego aktywnego cennika dla zlecenia</Alert>}
      <SelectSearcher
        name="orderRatePlan"
        label="Cennik do zlecenia"
        items={ratePlans}
        onSearchChange={() => {}}
        optionsObject={{ key: "ratePlanId", value: "description" }}
        placeholder="Szukaj"
        hideSearcher
        saveWholeObject
      />
      <TextArea name="orderRatePlan.remarks" label="Uwagi" readonly/>
    </Card.Header>
    <Card.Body>
      {orderRatePlan?.wastes?.length > 0 && <div><h5>Towary</h5><hr/></div>}
      <TableDomain
        items={orderRatePlan?.wastes ?? []}
        columns={wasteColumns}
        selectable={false}
        getRowClassName={(item) => item.price === null ? "table-danger" : ""}
      />
      {orderRatePlan?.services?.length > 0 && <div><h5>Usługi</h5><hr/></div>}
      <TableDomain
        items={orderRatePlan?.services ?? []}
        columns={serviceColumns}
        selectable={false}
        getRowClassName={(item) => item.price === null ? "table-danger" : ""}
      />
    </Card.Body>
  </Card>;
};
